import React from 'react';
import { Link } from 'react-router-dom';
import { Mic, Sparkles, Send, BarChart3, Globe, Calendar, Zap, Shield, Check, ArrowRight } from 'lucide-react';

const FeaturesPage = ({ onGetStarted }) => (
  <div>
    <section className="py-20 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <span className="font-label text-[var(--color-coral)] mb-3 inline-block">FEATURES</span>
        <h1 className="font-serif text-4xl md:text-5xl mb-4">Everything you need to become<br />the source AI trusts</h1>
        <p className="text-base text-[var(--color-muted)] max-w-2xl mx-auto">
          From voice note to published pillar in minutes. Every feature is designed to make your content AI-ready.
        </p>
      </div>
    </section>

    {/* Feature Deep Dives */}
    {[
      { icon: Mic, title: 'Voice-First Creation', tag: 'KEY DIFFERENTIATOR',
        desc: "Speak your expertise for 2 minutes. Stilos transcribes, structures, and expands your ideas into a full article — preserving your authentic voice while adding SEO and AEO/GEO optimization.",
        points: ['No writing skills required', 'Preserves your authentic voice and expertise', 'AI expands and structures your ideas', 'Works on any device — phone, tablet, desktop'] },
      { icon: Sparkles, title: 'AI Article Generation', tag: 'CORE FEATURE',
        desc: "Enter a title and keywords. Get a complete, publication-ready article optimized for both traditional search and AI citation. Every article includes FAQ sections, key takeaways, and structured formatting.",
        points: ['GPT-5.2 powered generation', 'AEO/GEO optimization built-in', 'FAQ sections for AI citation', 'Brand voice consistency'] },
      { icon: BarChart3, title: 'AEO & GEO Optimization', tag: 'KEY DIFFERENTIATOR',
        desc: "Answer Engine Optimization and Generative Engine Optimization built into every article. Stilos structures your content to be the source AI models cite.",
        points: ['FAQ structuring for AI extraction', 'Entity-rich content formatting', 'Q&A pattern optimization', 'Real-time AEO/GEO scoring'] },
      { icon: Globe, title: 'Brand Voice Profiles & Reporting', tag: 'KEY DIFFERENTIATOR',
        desc: "Define your brand's identity once. Every article matches your unique voice. Generate comprehensive brand reports with actionable Brand Kits — color palettes, typography, logo direction, and content strategy.",
        points: ['Save multiple brand voices', 'AI-generated Brand Kit with color palette', 'Typography and imagery recommendations', 'Actionable next steps for designers and marketers'] },
      { icon: Send, title: 'Multi-Platform Publishing', tag: 'WORKFLOW',
        desc: "Publish directly to WordPress from one dashboard. Schedule posts, manage your content calendar, and maintain consistent presence across platforms.",
        points: ['WordPress direct publishing', 'Content calendar with scheduling', 'Medium, LinkedIn, Webflow coming soon', 'One dashboard for everything'] },
      { icon: Zap, title: 'Progressive Web App', tag: 'ACCESSIBILITY',
        desc: "Stilos works beautifully on any device. Record voice notes on your morning walk, edit on your tablet, publish from your desk. Install it like a native app.",
        points: ['Works offline for drafts', 'Install on phone/tablet/desktop', 'Native app feel', 'Mobile-optimized recording'] },
    ].map((feature, idx) => (
      <section key={idx} className={`py-20 px-6 ${idx % 2 === 0 ? '' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto">
          <div className={`grid md:grid-cols-2 gap-12 items-center ${idx % 2 !== 0 ? 'md:[direction:rtl] md:[&>*]:[direction:ltr]' : ''}`}>
            <div>
              <span className="font-label text-[var(--color-coral)] mb-2 inline-block">{feature.tag}</span>
              <h2 className="font-serif text-2xl md:text-3xl mb-4">{feature.title}</h2>
              <p className="text-sm text-[var(--color-muted)] leading-relaxed mb-6">{feature.desc}</p>
              <ul className="space-y-2.5">
                {feature.points.map((point, pidx) => (
                  <li key={pidx} className="flex items-center gap-2.5 text-sm">
                    <Check className="w-4 h-4 text-[var(--color-coral)] flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-10 flex items-center justify-center bg-[var(--color-surface)]" style={{boxShadow: 'none', minHeight: 240}}>
              <feature.icon className="w-16 h-16 text-[var(--color-coral)] opacity-20" />
            </div>
          </div>
        </div>
      </section>
    ))}

    {/* CTA */}
    <section className="py-20 px-6 bg-white">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-serif text-3xl mb-4">Ready to become the source AI trusts?</h2>
        <p className="text-[var(--color-muted)] text-sm mb-8">Start free. 3 articles. No credit card required.</p>
        <button onClick={onGetStarted} className="btn btn-primary text-base px-8 py-3">
          Start your first pillar <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  </div>
);

export default FeaturesPage;
